import React, { createContext, useContext, ReactNode, useReducer, ReactElement } from 'react';
import { SurveyType, WorkflowSendingHours as AutomationSendingHours } from 'shared/types/graphql';
import { surveySettingsReducer } from './SurveySettingsReducer';
import { SurveyCopy } from '@televet/shared-types';

export enum SurveyEditorView {
  Compose = 'compose',
  Settings = 'settings',
  Preview = 'preview',
}

interface IAppointmentTypeSettings {
  id: string;
  isActive: boolean;
  automatedTriggerInMinutes: number;
  appointmentTypeId: string;
  appointmentTypeName: string;
}

export interface ISurveyComposeSettings {
  surveyName: string;
  introMessage: string;
  ratingQuestion: string;
  sentStatusId: string;
  positiveQuestion: string;
  positiveStatusId: string;
  negativeQuestion: string;
  negativeStatusId: string;
  sendHours: AutomationSendingHours;
}

export interface ISurveyGeneralSettings {
  isAutomatedTriggerForAllAppointmentTypes: boolean;
  frequencyInDays: number;
  showLinkToLeaveReviewNeutral: boolean;
  showLinkToLeaveReviewSatisfied: boolean;
  linkToLeaveReview?: string;
}

interface IChangedSettings {
  surveySettingChanged: boolean;
  ratingWorkflowChanged: boolean;
  positiveWorkflowChanged: boolean;
  negativeWorkflowChanged: boolean;
  surveyAppointmentTypeSettingIds: string[];
}

export interface ISurveySettingsContext {
  changedSettings: IChangedSettings;
  surveyEnabled: boolean;
  surveySettingId: string;
  surveyType: SurveyType;
  isLoading: boolean;
  isSaving: boolean;
  generalSettings: ISurveyGeneralSettings;
  composeSettings: ISurveyComposeSettings;
  appointmentTypeSettings: IAppointmentTypeSettings[];
  currentEditorView: SurveyEditorView;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch?: any;
}

const initialState: ISurveySettingsContext = {
  changedSettings: {
    surveySettingChanged: false,
    ratingWorkflowChanged: false,
    positiveWorkflowChanged: false,
    negativeWorkflowChanged: false,
    surveyAppointmentTypeSettingIds: [],
  },
  surveyEnabled: false,
  surveySettingId: '',
  surveyType: SurveyType.Csat,
  isLoading: false,
  isSaving: false,
  generalSettings: {
    isAutomatedTriggerForAllAppointmentTypes: false,
    frequencyInDays: 180,
    showLinkToLeaveReviewNeutral: false,
    showLinkToLeaveReviewSatisfied: false,
  },
  composeSettings: {
    surveyName: SurveyCopy.surveyName,
    introMessage: SurveyCopy.introMessage,
    ratingQuestion: SurveyCopy.ratingQuestion,
    sentStatusId: '',
    positiveQuestion: SurveyCopy.positiveQuestion,
    positiveStatusId: '',
    negativeQuestion: SurveyCopy.negativeQuestion,
    negativeStatusId: '',
    sendHours: AutomationSendingHours.BusinessHours,
  },
  currentEditorView: SurveyEditorView.Settings,
  appointmentTypeSettings: [],
};

const SurveySettingsContext = createContext<ISurveySettingsContext>(initialState);

interface Props {
  children: ReactNode;
}

function SurveySettingsContextProvider({ ...props }: Props): ReactElement {
  const [surveySettingsContext, dispatch] = useReducer(surveySettingsReducer, initialState);
  return <SurveySettingsContext.Provider value={{ ...surveySettingsContext, dispatch }} {...props} />;
}

export const useSurveySettingsContext = (): ISurveySettingsContext => {
  return useContext(SurveySettingsContext);
};

export default SurveySettingsContextProvider;
